// TwickEditorPage.js
// Full-page AI editor for Weavevo (media-first)
//
// This file implements the latest requested UX:
// - Media grid 2 columns, visual-only tiles, 10-by-10 pagination to reduce latency.
// - Left toolbox WITHOUT Canvas.
// - Panels content:
//   * Media
//   * Text styles (click to add overlay + timeline segment)
//   * Elements (Shapes / Stickers / Emoji / GIF-like) click-to-add overlay + timeline segment
//   * TTS (browser preview only) generates audio preview + adds TTS timeline segment (no backend generation)
//   * Record (UI shortcuts only)
// - Timeline lanes ALWAYS visible for: Audio / Vidéos / Images / Text / Elements / TTS.
// - Drag media from library into timeline.
// - Drag timeline blocks to reorder.
// - Resize blocks by stretching right handle.
// - Light timeline background (no black).
// - Transform panel:
//   * Fit works on preview (Fill/Fit/Crop -> objectFit).
//   * Orientation selector compact.
//   * Transitions selector UI inspired by your attachment.
// - AI montage enriched with useful toggles.
//
// Notes:
// - This is a symbolic editor UI. Real multi-clip playback/render should be handled by your backend FFmpeg pipeline.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from './AuthContext';

/* -------------------------------- Helpers -------------------------------- */

const withUploadsPath = (p) => {
  if (!p) return '';
  if (p.startsWith('http://') || p.startsWith('https://')) return p;
  const clean = p.replace(/^\/+/, '');
  if (clean.startsWith('uploads/') || clean.startsWith('upload/')) return `/${clean}`;
  return `/uploads/${clean}`;
};

const buildMediaSrc = (m) => {
  if (!m) return '';
  const cand =
    m.thumb_path ||
    m.thumbnail_url ||
    m.preview_path ||
    m.file_path ||
    m.location ||
    m.final_src ||
    '';
  return withUploadsPath(cand);
};

const isVideo = (m) => {
  const t = (m?.media_type || m?.type || '').toLowerCase();
  if (t.includes('video')) return true;
  if (t.includes('image')) return false;
  const fp = (m?.file_path || m?.location || '').toLowerCase();
  return fp.endsWith('.mp4') || fp.endsWith('.mov') || fp.endsWith('.mkv') || fp.endsWith('.webm');
};

const isAudio = (m) => {
  const t = (m?.media_type || m?.type || '').toLowerCase();
  if (t.includes('audio')) return true;
  const fp = (m?.file_path || m?.location || '').toLowerCase();
  return (
    fp.endsWith('.mp3') ||
    fp.endsWith('.wav') ||
    fp.endsWith('.aac') ||
    fp.endsWith('.ogg') ||
    fp.endsWith('.m4a')
  );
};

const isImage = (m) => !isVideo(m) && !isAudio(m);

const formatTime = (seconds) => {
  const s = Math.max(0, seconds || 0);
  const mins = Math.floor(s / 60);
  const sec = s - mins * 60;
  const secStr = sec.toFixed(2).padStart(5, '0');
  return `${mins}:${secStr}`;
};

const guessAutoDuration = (m) => {
  if (!m) return 3;
  if (m.duration_seconds && m.duration_seconds > 0) return Math.min(m.duration_seconds, 20);
  if (isAudio(m)) return 6;
  if (isVideo(m)) return 5;
  return 3;
};

/* -------------------------------- Toasts -------------------------------- */

function useToasts() {
  const [items, setItems] = useState([]);

  const addToast = (message, type = 'success', timeout = 2000) => {
    const id = Math.random().toString(36).slice(2);
    setItems((cur) => [...cur, { id, message, type }]);
    if (timeout) {
      setTimeout(() => {
        setItems((cur) => cur.filter((t) => t.id !== id));
      }, timeout);
    }
  };

  const Toasts = () => (
    <div
      style={{
        position: 'fixed',
        right: 16,
        bottom: 16,
        zIndex: 2050,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      {items.map((t) => (
        <div
          key={t.id}
          className="shadow-sm"
          style={{
            minWidth: 240,
            maxWidth: 420,
            padding: '8px 10px',
            borderRadius: 12,
            backgroundColor: '#ffffff',
            border:
              t.type === 'error'
                ? '1px solid rgba(220,53,69,.45)'
                : '1px solid rgba(25,135,84,.45)',
            fontSize: 12,
          }}
        >
          <div className="d-flex align-items-start">
            <div className="me-2 mt-1">
              {t.type === 'error' ? (
                <i className="bi bi-x-circle text-danger" />
              ) : (
                <i className="bi bi-check2-circle text-success" />
              )}
            </div>
            <div className="flex-grow-1">{t.message}</div>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary ms-2 py-0 px-1"
              onClick={() => setItems((cur) => cur.filter((x) => x.id !== t.id))}
            >
              <i className="bi bi-x-lg" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  return { addToast, Toasts };
}

/* ---------------------------- Text + Elements ---------------------------- */

const TEXT_STYLES = [
  { key: 'title', label: 'Title text', style: { fontSize: 34, fontWeight: 800, letterSpacing: 0.5 } },
  { key: 'regular', label: 'Regular text', style: { fontSize: 20, fontWeight: 500 } },
  { key: 'hand', label: 'Hand Write', style: { fontSize: 26, fontWeight: 600, fontFamily: 'cursive' } },
  { key: 'italic', label: 'Italic Text', style: { fontSize: 22, fontStyle: 'italic', fontWeight: 600 } },
  { key: 'underline', label: 'Underline', style: { fontSize: 22, textDecoration: 'underline', fontWeight: 700 } },
  { key: 'upper', label: 'UPPERCASE', style: { fontSize: 20, textTransform: 'uppercase', fontWeight: 800, letterSpacing: 1 } },
  { key: 'spacing', label: 'S p a c i n g', style: { fontSize: 20, fontWeight: 700, letterSpacing: 4 } },
  { key: 'classic', label: 'Classic', style: { fontSize: 22, fontWeight: 700 } },
  { key: 'meme', label: 'MEME', style: { fontSize: 26, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 1 } },
  { key: 'black', label: 'BLACK', style: { fontSize: 20, fontWeight: 800 } },
  { key: 'white', label: 'WHITE', style: { fontSize: 20, fontWeight: 800 } },
];

const defaultTextForStyle = (key) => {
  switch (key) {
    case 'meme':
      return 'YOUR MOMENT';
    case 'upper':
      return 'YOUR STORY';
    case 'title':
      return 'Mon voyage';
    case 'hand':
      return 'Souvenir';
    default:
      return 'Texte';
  }
};

const ELEMENT_LIBRARY = {
  shapes: [
    { key: 'rounded', label: 'Rounded', kind: 'shape', style: { borderRadius: 14 } },
    { key: 'circle', label: 'Circle', kind: 'shape', style: { borderRadius: 999 } },
    { key: 'pill', label: 'Pill', kind: 'shape', style: { borderRadius: 999 } },
    { key: 'outline', label: 'Outline', kind: 'shape', style: { borderRadius: 10, borderStyle: 'dashed' } },
  ],
  stickers: [
    { key: 'spark', label: '✨', kind: 'emoji' },
    { key: 'fire', label: '🔥', kind: 'emoji' },
    { key: 'heart', label: '❤️', kind: 'emoji' },
    { key: 'star', label: '⭐', kind: 'emoji' },
    { key: 'party', label: '🎉', kind: 'emoji' },
    { key: 'camera', label: '📸', kind: 'emoji' },
  ],
  emoji: [
    '😀','😎','🥳','🤩','😍','😭','😅','😤','🤝','🏆','🌍','✈️','🏖️','🏙️',
  ].map((e, i) => ({ key: `emo-${i}`, label: e, kind: 'emoji' })),
  gifs: [
    { key: 'gif-like-1', label: 'GIF', kind: 'badge' },
    { key: 'gif-like-2', label: 'Loop', kind: 'badge' },
    { key: 'gif-like-3', label: 'Boom', kind: 'badge' },
  ],
};

/* ------------------------------ Transitions ------------------------------ */

const TRANSITIONS = [
  { key: 'none', label: 'Aucune', icon: 'bi-slash-circle' },
  { key: 'fade', label: 'Fade', icon: 'bi-droplet' },
  { key: 'slide-left', label: 'Slide L', icon: 'bi-arrow-left-right' },
  { key: 'slide-right', label: 'Slide R', icon: 'bi-arrow-left-right' },
  { key: 'zoom', label: 'Zoom', icon: 'bi-zoom-in' },
  { key: 'radial', label: 'Radial', icon: 'bi-record-circle' },
];

/* --------------------------------- Main ---------------------------------- */

export default function TwickEditorPage(props = {}) {
  const {
    embedded = false,
    montageParams = null,
    projectId: projectIdProp,
  } = props;

  const routeParams = useParams();
  const projectId = String(projectIdProp ?? montageParams?.projectId ?? routeParams?.id ?? '');

  const { token } = useAuth();
  const { addToast, Toasts } = useToasts();

  const [project, setProject] = useState(null);
  const [mediaList, setMediaList] = useState([]);

  // Linear timeline segments
  // Segment shape:
  // { kind:'media'|'text'|'element'|'tts', mediaId?, payload?, duration, transition? }
  const [timeline, setTimeline] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);

  // Preview config
  const [aspect, setAspect] = useState('landscape'); // portrait | square | landscape
  const [previewFit, setPreviewFit] = useState('cover'); // cover | contain | scale-down

  // Panels
  const [openTransform, setOpenTransform] = useState(true);
  const [openAiPanel, setOpenAiPanel] = useState(true);

  // Loading
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Playback (symbolic)
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  // Library filters
  const [toolActive, setToolActive] = useState('media');
  const [mediaFilter, setMediaFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [hoverMediaId, setHoverMediaId] = useState(null);

  // Pagination
  const MEDIA_PAGE_SIZE = 10;
  const [mediaPage, setMediaPage] = useState(1);

  // Text overlays
  const [textOverlays, setTextOverlays] = useState([]); // {id, styleKey, text, x,y}
  const [selectedTextId, setSelectedTextId] = useState(null);

  // Element overlays
  const [elementOverlays, setElementOverlays] = useState([]); // {id, category, itemKey, label, x,y}
  const [selectedElementId, setSelectedElementId] = useState(null);

  // TTS state (browser preview only)
  const [ttsLanguage, setTtsLanguage] = useState('fr-FR');
  const [ttsVoice, setTtsVoice] = useState('default');
  const [ttsText, setTtsText] = useState('Bonjour, ceci est une voix de démonstration.');
  const [ttsSpeaking, setTtsSpeaking] = useState(false);

  // Record state (UI only)
  const [recordMode, setRecordMode] = useState('audio');

  // AI montage options
  const [aiMode, setAiMode] = useState('linear');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiMessage, setAiMessage] = useState('');
  const [aiUseTransitions, setAiUseTransitions] = useState(true);
  const [aiPrioritizeVideos, setAiPrioritizeVideos] = useState(true);
  const [aiMaxClips, setAiMaxClips] = useState(24);
  const [aiAutoFit, setAiAutoFit] = useState(true);
  const [aiStyleIntensity, setAiStyleIntensity] = useState(60); // 0..100

  // External audio params (sync from JourneyAdmin Advanced mode)
  const [externalVoice, setExternalVoice] = useState(null);
  const [externalMusic, setExternalMusic] = useState(null);
  const [externalAudioMix, setExternalAudioMix] = useState(null);
  const [externalMusicVolume, setExternalMusicVolume] = useState(null);

  const hydratedFromParamsRef = useRef(false);
 // 0..100

  // Transition selection for new segments
  const [defaultTransition, setDefaultTransition] = useState('fade');

  // Resize drag ref
  const resizingRef = useRef(null);

  // Persist keys
  const storageKeyTimeline = useMemo(
    () => (projectId ? `weavevo:project:${projectId}:ai-editor-timeline` : 'weavevo:ai-editor-timeline'),
    [projectId]
  );

  /* -------------------------- Load persisted state ------------------------- */

  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKeyTimeline);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) setTimeline(arr);
      }
      const asp = localStorage.getItem('weavevo:ai-editor-aspect');
      if (asp) setAspect(asp);
      const fit = localStorage.getItem('weavevo:ai-editor-fit');
      if (fit) setPreviewFit(fit);
      const mode = localStorage.getItem('weavevo:ai-editor-ai-mode');
      if (mode) setAiMode(mode);
    } catch {
      // ignore
    }
  }, [storageKeyTimeline]);

  useEffect(() => {
    try {
      localStorage.setItem(storageKeyTimeline, JSON.stringify(timeline));
      localStorage.setItem('weavevo:ai-editor-aspect', aspect);
      localStorage.setItem('weavevo:ai-editor-fit', previewFit);
      localStorage.setItem('weavevo:ai-editor-ai-mode', aiMode);
    } catch {
      // ignore
    }
  }, [timeline, aspect, previewFit, aiMode, storageKeyTimeline]);

  /* ------------------------------ Load project ----------------------------- */

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!projectId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError('');

      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const [pRes, mRes] = await Promise.all([
          fetch(`/projects/${projectId}`, { headers }),
          fetch(`/projects/${projectId}/media/list`, { headers }),
        ]);

        if (!pRes.ok) throw new Error(`Projet: HTTP ${pRes.status}`);
        if (!mRes.ok) throw new Error(`Médias: HTTP ${mRes.status}`);

        const ctP = (pRes.headers.get('content-type') || '').toLowerCase();
        const ctM = (mRes.headers.get('content-type') || '').toLowerCase();
        if (!ctP.includes('application/json') || !ctM.includes('application/json')) {
          throw new Error('Réponse non JSON.');
        }

        const proj = await pRes.json();
        const medias = await mRes.json();

        if (!cancelled) {
          setProject(proj || null);
          setMediaList(Array.isArray(medias) ? medias : []);
          setMediaPage(1);
        }
      } catch (e) {
        console.error('[AI Editor] load error:', e);
        if (!cancelled) {
          setError("Impossible de charger le projet ou ses médias.");
          setMediaList([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [projectId, token]);

  /* ------------------------------ Derived maps ----------------------------- */

  const mediaById = useMemo(() => {
    const map = {};
    (mediaList || []).forEach((m) => {
      const mid = m.id ?? m.media_id;
      if (!mid) return;
      map[String(mid)] = m;
    });
    return map;
  }, [mediaList]);

  /* -------------------- Advanced mode: parent parameters sync -------------------- */

  useEffect(() => {
    if (!montageParams) return;

    // Always refresh the external audio summary for the UI
    setExternalVoice(montageParams.voice ?? null);
    setExternalMusic(montageParams.music ?? null);
    setExternalAudioMix(montageParams.audio_mix_option ?? null);
    setExternalMusicVolume(
      typeof montageParams.music_volume !== 'undefined' ? montageParams.music_volume : null
    );

    if (hydratedFromParamsRef.current) return;

    const ids =
      montageParams.selectedMediaIds ||
      montageParams.media_order ||
      montageParams.selected_media_ids ||
      [];

    const normalizedIds = Array.isArray(ids) ? ids.map((x) => String(x)) : [];

    // Only auto-build timeline if empty (avoid overwriting user edits)
    if ((timeline?.length || 0) === 0 && normalizedIds.length) {
      const segs = normalizedIds.map((mid) => {
        const m = mediaById[mid] || { id: mid };
        return {
          kind: 'media',
          mediaId: m.id ?? mid,
          duration: guessAutoDuration(m),
          transition: montageParams.transition || defaultTransition || 'fade',
        };
      });
      setTimeline(segs);
      setSelectedIndex(segs.length ? 0 : null);
    }

    // Best-effort aspect sync from parent export aspect
    if (typeof montageParams.export_aspect === 'string') {
      const asp = montageParams.export_aspect;
      if (asp.includes('9:16')) setAspect('portrait');
      else if (asp.includes('1:1')) setAspect('square');
      else if (asp.includes('16:9')) setAspect('landscape');
    }

    hydratedFromParamsRef.current = true;
  }, [montageParams, mediaById, defaultTransition, timeline?.length]);


  useEffect(() => {
    if (toolActive === 'audio') setMediaFilter('audio');
    else if (toolActive === 'video') setMediaFilter('video');
    else if (toolActive === 'image') setMediaFilter('image');
    else if (toolActive === 'media') setMediaFilter('all');
  }, [toolActive]);

  const filteredMediaList = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    const base = mediaList.filter((m) => {
      if (mediaFilter === 'all') return true;
      if (mediaFilter === 'video') return isVideo(m);
      if (mediaFilter === 'image') return isImage(m);
      if (mediaFilter === 'audio') return isAudio(m);
      return true;
    });

    if (!q) return base;

    return base.filter((m) => {
      const title = (m?.title || '').toLowerCase();
      const loc = (m?.location || '').toLowerCase();
      const type = (m?.media_type || m?.type || '').toLowerCase();
      const fp = (m?.file_path || '').toLowerCase();
      return title.includes(q) || loc.includes(q) || type.includes(q) || fp.includes(q);
    });
  }, [mediaList, mediaFilter, searchQuery]);

  const totalMediaPages = Math.max(1, Math.ceil(filteredMediaList.length / MEDIA_PAGE_SIZE));

  const pagedMediaList = useMemo(() => {
    const start = (mediaPage - 1) * MEDIA_PAGE_SIZE;
    return filteredMediaList.slice(start, start + MEDIA_PAGE_SIZE);
  }, [filteredMediaList, mediaPage]);

  useEffect(() => {
    if (mediaPage > totalMediaPages) setMediaPage(totalMediaPages);
  }, [totalMediaPages]);

  /* -------------------------- Timeline computed segments ------------------- */

  const segments = useMemo(() => {
    let acc = 0;
    return timeline.map((seg) => {
      const start = acc;
      const dur = seg.duration || 3;
      acc += dur;

      const media = seg.kind === 'media' && seg.mediaId ? mediaById[String(seg.mediaId)] : null;

      return {
        ...seg,
        start,
        end: start + dur,
        media,
      };
    });
  }, [timeline, mediaById]);

  const totalDuration =
    segments.reduce((sum, s) => sum + (s.duration || 0), 0) || (mediaList.length ? 15 : 10);

  const currentClamped = totalDuration > 0 ? Math.min(Math.max(currentTime, 0), totalDuration) : 0;

  const activeIndex = useMemo(() => {
    if (!segments.length) return null;
    if (isPlaying && totalDuration > 0) {
      const idx = segments.findIndex((s) => currentClamped >= s.start && currentClamped < s.end);
      return idx === -1 ? segments.length - 1 : idx;
    }
    if (selectedIndex != null && segments[selectedIndex]) return selectedIndex;
    return 0;
  }, [segments, isPlaying, currentClamped, selectedIndex, totalDuration]);

  const selectedSegment = activeIndex != null ? segments[activeIndex] : null;
  const selectedMedia = selectedSegment?.media || null;
  const selectedSrc = buildMediaSrc(selectedMedia);
  const selectedIsVideo = selectedMedia ? isVideo(selectedMedia) : false;

  /* ----------------------------- Preview aspect ---------------------------- */

  const canvasAspectStyle =
    aspect === 'portrait'
      ? { width: '100%', aspectRatio: '9 / 16' }
      : aspect === 'square'
      ? { width: '100%', aspectRatio: '1 / 1' }
      : { width: '100%', aspectRatio: '16 / 9' };

  const objectFit = useMemo(() => {
    if (previewFit === 'contain') return 'contain';
    if (previewFit === 'scale-down') return 'scale-down';
    return 'cover';
  }, [previewFit]);

  /* ----------------------------- Timeline metrics -------------------------- */

  const pxPerSecond = 30;
  const timelineWidth = totalDuration > 0 ? Math.max(totalDuration * pxPerSecond, 720) : 720;
  const playheadLeft = totalDuration > 0 ? (currentClamped / totalDuration) * timelineWidth : 0;

  const tracks = useMemo(
    () => [
      { id: 'audio', label: 'Audio', icon: 'bi-music-note-beamed', match: (s) => s.kind === 'media' && s.media && isAudio(s.media) },
      { id: 'video', label: 'Vidéos', icon: 'bi-camera-video', match: (s) => s.kind === 'media' && s.media && isVideo(s.media) },
      { id: 'image', label: 'Images', icon: 'bi-image', match: (s) => s.kind === 'media' && s.media && isImage(s.media) },
      { id: 'text', label: 'Text', icon: 'bi-fonts', match: (s) => s.kind === 'text' },
      { id: 'elements', label: 'Elements', icon: 'bi-stars', match: (s) => s.kind === 'element' },
      { id: 'tts', label: 'TTS', icon: 'bi-soundwave', match: (s) => s.kind === 'tts' },
    ],
    []
  );

  // 🌿 Show only lanes that have associated selected items/segments
  const visibleTracks = useMemo(() => {
    if (!segments.length) return [];
    return tracks.filter((t) => segments.some((s) => t.match(s)));
  }, [tracks, segments]);

  /* ----------------------------- Playback tick ----------------------------- */

  useEffect(() => {
    if (!isPlaying || totalDuration <= 0) return;
    const step = 0.1;
    const id = setInterval(() => {
      setCurrentTime((t) => {
        const next = t + step;
        if (next >= totalDuration) return 0;
        return next;
      });
    }, step * 1000);
    return () => clearInterval(id);
  }, [isPlaying, totalDuration]);

  /* -------------------------- Timeline actions ----------------------------- */

  const addMediaClip = (m, insertAtIndex = null) => {
    const mid = m.id ?? m.media_id;
    if (!mid) return;
    const baseDur = guessAutoDuration(m);

    const seg = {
      kind: 'media',
      mediaId: mid,
      duration: baseDur,
      transition: defaultTransition,
    };

    setTimeline((cur) => {
      if (insertAtIndex == null || insertAtIndex < 0 || insertAtIndex > cur.length) {
        return [...cur, seg];
      }
      const next = [...cur];
      next.splice(insertAtIndex, 0, seg);
      return next;
    });

    setIsPlaying(false);
    addToast('Clip média ajouté à la timeline.', 'success');
  };

  const addTextOverlay = (styleKey) => {
    const styleDef = TEXT_STYLES.find((s) => s.key === styleKey) || TEXT_STYLES[0];
    const id = Math.random().toString(36).slice(2);

    const overlay = {
      id,
      styleKey: styleDef.key,
      text: defaultTextForStyle(styleDef.key),
      x: 50,
      y: 18,
    };

    setTextOverlays((cur) => [...cur, overlay]);
    setSelectedTextId(id);

    const seg = {
      kind: 'text',
      payload: { overlayId: id, styleKey: styleDef.key, text: overlay.text },
      duration: 3,
      transition: defaultTransition,
    };
    setTimeline((cur) => [...cur, seg]);

    setIsPlaying(false);
    addToast('Texte ajouté (overlay + timeline).', 'success');
  };

  const addElementOverlay = (category, item) => {
    const id = Math.random().toString(36).slice(2);
    const overlay = {
      id,
      category,
      itemKey: item.key,
      label: item.label,
      kind: item.kind,
      x: 50,
      y: 28,
    };

    setElementOverlays((cur) => [...cur, overlay]);
    setSelectedElementId(id);

    const seg = {
      kind: 'element',
      payload: { overlayId: id, category, itemKey: item.key, label: item.label, kind: item.kind },
      duration: 3,
      transition: defaultTransition,
    };
    setTimeline((cur) => [...cur, seg]);

    setIsPlaying(false);
    addToast('Element ajouté (overlay + timeline).', 'success');
  };

  const addTtsSegment = () => {
    const id = Math.random().toString(36).slice(2);
    const seg = {
      kind: 'tts',
      payload: { ttsId: id, language: ttsLanguage, voice: ttsVoice, text: ttsText },
      duration: 6,
      transition: defaultTransition,
    };
    setTimeline((cur) => [...cur, seg]);
    setIsPlaying(false);
    addToast('TTS ajouté à la timeline (preview navigateur).', 'success');
  };

  const removeSegment = (idx) => {
    setTimeline((cur) => cur.filter((_, i) => i !== idx));
    if (selectedIndex === idx) setSelectedIndex(null);
  };

  const clearTimeline = () => {
    setTimeline([]);
    setSelectedIndex(null);
    setCurrentTime(0);
    setIsPlaying(false);
  };

  const moveClipTo = (from, to) => {
   	if (from === to) return;
    setTimeline((cur) => {
      if (from < 0 || from >= cur.length || to < 0 || to >= cur.length) return cur;
      const next = [...cur];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };

  /* --------------------------- Drag & Drop -------------------------------- */

  const handleMediaDragStart = (e, m) => {
    const mid = m.id ?? m.media_id;
    if (!mid) return;
    e.dataTransfer.setData('weavevo-drag-type', 'media');
    e.dataTransfer.setData('weavevo-media-id', String(mid));
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleSegmentDragStart = (e, index) => {
    e.dataTransfer.setData('weavevo-drag-type', 'segment');
    e.dataTransfer.setData('weavevo-seg-index', String(index));
    e.dataTransfer.effectAllowed = 'move';
  };

  const allowDrop = (e) => {
    e.preventDefault();
  };

  const handleLaneDrop = (e) => {
    e.preventDefault();
    const type = e.dataTransfer.getData('weavevo-drag-type');

    if (type === 'media') {
      const mid = e.dataTransfer.getData('weavevo-media-id');
      const m = mediaById[mid];
      if (m) addMediaClip(m);
      return;
    }
  };

  const handleBlockDrop = (e, targetIndex) => {
    e.preventDefault();
    const type = e.dataTransfer.getData('weavevo-drag-type');

    if (type === 'media') {
      const mid = e.dataTransfer.getData('weavevo-media-id');
      const m = mediaById[mid];
      if (m) addMediaClip(m, targetIndex);
      setSelectedIndex(targetIndex);
      return;
    }

    if (type === 'segment') {
      const from = Number(e.dataTransfer.getData('weavevo-seg-index'));
      if (Number.isFinite(from)) {
        moveClipTo(from, targetIndex);
        setSelectedIndex(targetIndex);
        setIsPlaying(false);
      }
    }
  };

  /* ----------------------------- Resize (stretch) -------------------------- */

  const startResize = (e, index, startDur) => {
    e.stopPropagation();
    e.preventDefault();
    resizingRef.current = {
      index,
      startX: e.clientX,
      startDuration: startDur || 3,
    };
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!resizingRef.current) return;
      const { index, startX, startDuration } = resizingRef.current;
      const dx = e.clientX - startX;
      const deltaSeconds = dx / pxPerSecond;
      const nextDur = Math.max(0.5, Math.min(startDuration + deltaSeconds, 30));

      setTimeline((cur) => cur.map((seg, i) => (i === index ? { ...seg, duration: nextDur } : seg)));
    };

    const onUp = () => {
      resizingRef.current = null;
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [pxPerSecond]);

  /* ----------------------------- AI Montage -------------------------------- */

  const buildFallbackAiTimeline = (mode) => {
    if (!mediaList.length) return [];
    let medias = [...mediaList];

    if (aiPrioritizeVideos) {
      const vids = medias.filter(isVideo);
      const imgs = medias.filter(isImage);
      const auds = medias.filter(isAudio);
      medias = [...vids, ...imgs, ...auds];
    }

    if (mode === 'social') {
      for (let i = medias.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [medias[i], medias[j]] = [medias[j], medias[i]];
      }
    }

    medias = medias.slice(0, Math.min(aiMaxClips, medias.length));

    return medias
      .map((m) => {
        const mid = m.id ?? m.media_id;
        if (!mid) return null;
        const video = isVideo(m);
        const audio = isAudio(m);

        let dur = 3.2;
        if (mode === 'social') dur = video ? 2.0 : audio ? 3.0 : 1.6;
        if (mode === 'story') dur = video ? 4.8 : audio ? 6.0 : 3.2;

        if (m.duration_seconds) dur = Math.min(Math.max(m.duration_seconds * 0.6, 2), 10);

        return {
          kind: 'media',
          mediaId: mid,
          duration: Math.max(0.5, Math.min(dur, 30)),
          transition: aiUseTransitions ? defaultTransition : 'none',
        };
      })
      .filter(Boolean);
  };

  const handleAiAutoMontage = async () => {
    if (!projectId) {
      addToast('Projet introuvable pour le montage IA.', 'error');
      return;
    }
    if (!mediaList.length) {
      addToast('Ajoute des médias au projet avant de lancer le montage IA.', 'error');
      return;
    }

    setAiLoading(true);
    setAiMessage('');
    setIsPlaying(false);

    const payload = {
      mode: aiMode,
      media_ids: mediaList.map((m) => m.id ?? m.media_id).filter(Boolean),
      options: {
        useTransitions: aiUseTransitions,
        prioritizeVideos: aiPrioritizeVideos,
        maxClips: aiMaxClips,
        autoFit: aiAutoFit,
        styleIntensity: aiStyleIntensity,
        defaultTransition,
      },
    };

    let usedFallback = false;

    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const res = await fetch(`/projects/${projectId}/ai/montage`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      const ct = (res.headers.get('content-type') || '').toLowerCase();
      if (!res.ok || !ct.includes('application/json')) {
        usedFallback = true;
      } else {
        const data = await res.json();
        if (!data || !Array.isArray(data.segments)) {
          usedFallback = true;
        } else {
          const norm = data.segments
            .map((seg) => {
              const mediaId = seg.mediaId ?? seg.media_id;
              const dur = Number(seg.duration) || 3;
              if (!mediaId) return null;
              return {
                kind: 'media',
                mediaId,
                duration: Math.max(0.5, Math.min(dur, 30)),
                transition: aiUseTransitions ? (seg.transition || defaultTransition) : 'none',
              };
            })
            .filter(Boolean);

          if (!norm.length) usedFallback = true;
          else {
            if (aiAutoFit) setPreviewFit('cover');
            setTimeline(norm);
            setSelectedIndex(0);
            setCurrentTime(0);
            addToast('Montage IA appliqué (backend).', 'success');
            setAiMessage('Timeline générée par le backend IA.');
          }
        }
      }
    } catch (e) {
      console.error('[AI Editor] montage IA error:', e);
      usedFallback = true;
    } finally {
      if (usedFallback) {
        if (aiAutoFit) setPreviewFit('cover');
        const fallback = buildFallbackAiTimeline(aiMode);
        setTimeline(fallback);
        setSelectedIndex(0);
        setCurrentTime(0);
        addToast('Montage IA généré côté client (fallback).', 'success');
        setAiMessage('Suggestion locale appliquée.');
      }
      setAiLoading(false);
    }
  };

  /* ----------------------------- TTS preview ------------------------------- */

  const speakTts = () => {
    try {
      if (!('speechSynthesis' in window)) {
        addToast("TTS navigateur non supporté.", 'error');
        return;
      }
      window.speechSynthesis.cancel();
      const utter = new SpeechSynthesisUtterance(ttsText || '');
      utter.lang = ttsLanguage;
      utter.onstart = () => setTtsSpeaking(true);
      utter.onend = () => setTtsSpeaking(false);
      utter.onerror = () => setTtsSpeaking(false);
      window.speechSynthesis.speak(utter);
    } catch {
      setTtsSpeaking(false);
    }
  };

  const stopTts = () => {
    try {
      window.speechSynthesis.cancel();
    } catch {}
    setTtsSpeaking(false);
  };

  /* ----------------------------- UI components ----------------------------- */

  const ToolboxButton = ({ icon, label, onClick, active }) => (
    <button
      type="button"
      onClick={onClick}
      className="btn w-100 d-flex flex-column align-items-center justify-content-center"
      style={{
        padding: '10px 6px',
        borderRadius: 12,
        background: active ? 'rgba(13,110,253,.12)' : 'transparent',
        border: active ? '1px solid rgba(13,110,253,.25)' : '1px solid transparent',
        color: active ? '#0d6efd' : '#6c757d',
      }}
      title={label}
    >
      <i className={`bi ${icon}`} style={{ fontSize: 18, lineHeight: 1 }} />
      <span style={{ fontSize: 9, marginTop: 4 }}>{label}</span>
    </button>
  );

  const TextStyleTile = ({ item }) => {
    const isWhite = item.key === 'white';
    return (
      <button
        type="button"
        onClick={() => addTextOverlay(item.key)}
        className="btn text-start"
        style={{
          borderRadius: 10,
          padding: '10px 8px',
          background: isWhite ? '#ffffff' : '#171a20',
          color: isWhite ? '#111' : '#fff',
          border: isWhite ? '1px solid rgba(0,0,0,.08)' : '1px solid rgba(255,255,255,.06)',
          minHeight: 48,
          fontWeight: 700,
          fontSize: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          letterSpacing: item.key === 'spacing' ? 1.2 : undefined,
          fontStyle: item.key === 'italic' ? 'italic' : undefined,
          textDecoration: item.key === 'underline' ? 'underline' : undefined,
          textTransform: item.key === 'upper' ? 'uppercase' : undefined,
          fontFamily: item.key === 'hand' ? 'cursive' : undefined,
        }}
      >
        {item.label}
      </button>
    );
  };

  const RecordCard = ({ keyName, title, subtitle, icon, active, onClick }) => (
    <button
      type="button"
      onClick={onClick}
      className="btn text-start w-100"
      style={{
        borderRadius: 12,
        padding: '12px 12px',
        border: active ? '1px solid rgba(13,110,253,.35)' : '1px solid rgba(0,0,0,.08)',
        background: active ? 'rgba(13,110,253,.06)' : '#fff',
      }}
      data-key={keyName}
    >
      <div className="d-flex align-items-center gap-2">
        <div
          className="d-flex align-items-center justify-content-center"
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            background: active ? 'rgba(13,110,253,.12)' : '#f5f7fb',
          }}
        >
          <i className={`bi ${icon}`} />
        </div>
        <div className="min-w-0">
          <div className="small fw-semibold text-truncate">{title}</div>
          <div className="small text-muted text-truncate">{subtitle}</div>
        </div>
      </div>
    </button>
  );

  /* ----------------------------- Render blocks ----------------------------- */

  const renderLeftPanel = () => {
    // MEDIA panel by default; other tool panels replace it.

    if (toolActive === 'text') {
      return (
        <>
          <div className="d-flex align-items-center gap-2 mb-2">
            <h6 className="mb-0">Text</h6>
            <span className="badge bg-light text-dark">Styles</span>
            <span className="small text-muted ms-1">Clic = ajoute overlay + timeline</span>
          </div>

          <div className="border rounded-4 p-2" style={{ background: '#0f1115' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: 8,
              }}
            >
              {TEXT_STYLES.map((s) => (
                <TextStyleTile key={s.key} item={s} />
              ))}
            </div>
          </div>

          <div className="mt-3">
            <div className="d-flex align-items-center justify-content-between">
              <span className="small fw-semibold">Overlays texte</span>
              <span className="badge bg-light text-dark">{textOverlays.length}</span>
            </div>
            <div className="vstack gap-2 mt-2">
              {textOverlays.length === 0 ? (
                <div className="small text-muted">Aucun overlay texte.</div>
              ) : (
                textOverlays.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className="btn btn-sm btn-outline-secondary text-start"
                    onClick={() => setSelectedTextId(t.id)}
                    style={{ borderRadius: 10 }}
                  >
                    <i className="bi bi-fonts me-1" />
                    {t.text?.slice(0, 24) || 'Texte'}
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      );
    }

    if (toolActive === 'elements') {
      return (
        <>
          <div className="d-flex align-items-center gap-2 mb-2">
            <h6 className="mb-0">Elements</h6>
            <span className="badge bg-light text-dark">Bibliothèque</span>
            <span className="small text-muted ms-1">Clic = ajoute overlay + timeline</span>
          </div>

          {/* Shapes */}
          <div className="mb-2">
            <div className="small fw-semibold mb-1">Shapes</div>
            <div className="d-flex flex-wrap gap-2">
              {ELEMENT_LIBRARY.shapes.map((it) => (
                <button
                  key={it.key}
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  style={{ borderRadius: 10 }}
                  onClick={() => addElementOverlay('shapes', it)}
                >
                  {it.label}
                </button>
              ))}
            </div>
          </div>

          {/* Stickers */}
          <div className="mb-2">
            <div className="small fw-semibold mb-1">Stickers</div>
            <div className="d-flex flex-wrap gap-2">
              {ELEMENT_LIBRARY.stickers.map((it) => (
                <button
                  key={it.key}
                  type="button"
                  className="btn btn-sm"
                  style={{
                    borderRadius: 10,
                    background: '#f6f8fc',
                    border: '1px solid rgba(0,0,0,.06)',
                  }}
                  onClick={() => addElementOverlay('stickers', it)}
                >
                  <span style={{ fontSize: 18 }}>{it.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Emoji */}
          <div className="mb-2">
            <div className="small fw-semibold mb-1">Emoji</div>
            <div className="d-flex flex-wrap gap-2">
              {ELEMENT_LIBRARY.emoji.map((it) => (
                <button
                  key={it.key}
                  type="button"
                  className="btn btn-sm"
                  style={{
                    borderRadius: 10,
                    background: '#f6f8fc',
                    border: '1px solid rgba(0,0,0,.06)',
                  }}
                  onClick={() => addElementOverlay('emoji', it)}
                >
                  <span style={{ fontSize: 18 }}>{it.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* GIF-like */}
          <div>
            <div className="small fw-semibold mb-1">GIFs</div>
            <div className="d-flex flex-wrap gap-2">
              {ELEMENT_LIBRARY.gifs.map((it) => (
                <button
                  key={it.key}
                  type="button"
                  className="btn btn-sm btn-dark"
                  style={{ borderRadius: 10 }}
                  onClick={() => addElementOverlay('gifs', it)}
                >
                  {it.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-3">
            <div className="d-flex align-items-center justify-content-between">
              <span className="small fw-semibold">Overlays elements</span>
              <span className="badge bg-light text-dark">{elementOverlays.length}</span>
            </div>
          </div>
        </>
      );
    }

    if (toolActive === 'tts') {
      return (
        <>
          <div className="d-flex align-items-center gap-2 mb-2">
            <h6 className="mb-0">TTS</h6>
            <span className="badge bg-light text-dark">Preview navigateur</span>
          </div>

          <div className="border rounded-4 p-3" style={{ background: '#fbfdff' }}>
            <div className="row g-2">
              <div className="col-6">
                <label className="form-label small fw-semibold">Language</label>
                <select
                  className="form-select form-select-sm"
                  value={ttsLanguage}
                  onChange={(e) => setTtsLanguage(e.target.value)}
                >
                  <option value="fr-FR">French (FR)</option>
                  <option value="en-US">English (US)</option>
                  <option value="en-GB">English (UK)</option>
                  <option value="ar-SA">Arabic</option>
                  <option value="es-ES">Spanish</option>
                </select>
              </div>
              <div className="col-6">
                <label className="form-label small fw-semibold">Voice</label>
                <select
                  className="form-select form-select-sm"
                  value={ttsVoice}
                  onChange={(e) => setTtsVoice(e.target.value)}
                >
                  <option value="default">Default</option>
                  <option value="female">Female-ish</option>
                  <option value="male">Male-ish</option>
                </select>
              </div>
            </div>

            <div className="mt-2">
              <label className="form-label small fw-semibold">Text</label>
              <textarea
                rows={5}
                className="form-control form-control-sm"
                value={ttsText}
                onChange={(e) => setTtsText(e.target.value)}
              />
            </div>

            <div className="d-flex gap-2 mt-2">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={ttsSpeaking ? stopTts : speakTts}
              >
                <i className={`bi ${ttsSpeaking ? 'bi-stop-fill' : 'bi-play-fill'} me-1`} />
                {ttsSpeaking ? 'Stop' : 'Preview'}
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={addTtsSegment}
                disabled={!ttsText.trim()}
              >
                <i className="bi bi-plus-lg me-1" />
                Add to timeline
              </button>
            </div>

            <div className="small text-muted mt-2">
              Le bouton ajoute un segment TTS symbolique. La génération audio réelle peut être branchée plus tard côté backend.
            </div>
          </div>
        </>
      );
    }

    if (toolActive === 'record') {
      return (
        <>
          <div className="d-flex align-items-center gap-2 mb-2">
            <h6 className="mb-0">Record</h6>
            <span className="badge bg-light text-dark">UI</span>
            <span className="small text-muted ms-1">Intégration backend possible</span>
          </div>

          <div className="vstack gap-2">
            <RecordCard
              keyName="audio"
              title="Record audio"
              subtitle="Microphone"
              icon="bi-mic"
              active={recordMode === 'audio'}
              onClick={() => setRecordMode('audio')}
            />
            <RecordCard
              keyName="camera"
              title="Record camera"
              subtitle="Webcam"
              icon="bi-camera-video"
              active={recordMode === 'camera'}
              onClick={() => setRecordMode('camera')}
            />
            <RecordCard
              keyName="screen"
              title="Record screen"
              subtitle="Capture écran"
              icon="bi-display"
              active={recordMode === 'screen'}
              onClick={() => setRecordMode('screen')}
            />
            <RecordCard
              keyName="screen-cam"
              title="Screen + camera"
              subtitle="Overlay webcam"
              icon="bi-aspect-ratio"
              active={recordMode === 'screen-cam'}
              onClick={() => setRecordMode('screen-cam')}
            />
          </div>

          <div className="border rounded-4 p-3 mt-2" style={{ background: '#fbfdff' }}>
            <div className="small fw-semibold">Mode sélectionné</div>
            <div className="small text-muted">
              {recordMode === 'audio'
                ? 'Enregistre un commentaire audio.'
                : recordMode === 'camera'
                ? 'Enregistre une vidéo caméra.'
                : recordMode === 'screen'
                ? 'Enregistre l’écran.'
                : 'Enregistre l’écran avec webcam incrustée.'}
            </div>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm mt-2"
              onClick={() => addToast('UI Record: branchement backend à prévoir.', 'success')}
            >
              <i className="bi bi-record-circle me-1" />
              Start (UI)
            </button>
          </div>
        </>
      );
    }

    // MEDIA default panel
    return (
      <>
        <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
          <h6 className="mb-0">Médias du projet</h6>
          <span className="badge bg-light text-dark">
            {filteredMediaList.length}/{mediaList.length}
          </span>
          <span className="small text-muted d-none d-lg-inline ms-1">
            Drag ou clic = ajoute à la timeline
          </span>
        </div>

        <div className="mb-2">
          <input
            className="form-control form-control-sm"
            placeholder="Rechercher un média..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div
          id="ai-editor-content-panel"
          style={{
            maxHeight: 'calc(100vh - 360px)',
            minHeight: 260,
            overflowY: 'auto',
            paddingRight: 4,
          }}
        >
          {pagedMediaList.length === 0 ? (
            <div className="text-muted small">Aucun média pour ce filtre.</div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: 10,
              }}
            >
              {pagedMediaList.map((m) => {
                const mid = m.id ?? m.media_id;
                const src = buildMediaSrc(m);
                const video = isVideo(m);
                const audio = isAudio(m);

                return (
                  <div
                    key={mid}
                    role="button"
                    tabIndex={0}
                    draggable
                    onDragStart={(e) => handleMediaDragStart(e, m)}
                    onMouseEnter={() => setHoverMediaId(mid)}
                    onMouseLeave={() => setHoverMediaId(null)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        addMediaClip(m);
                      }
                    }}
                    onClick={() => addMediaClip(m)}
                    className="border rounded-4 overflow-hidden position-relative bg-light"
                    style={{
                      cursor: 'pointer',
                      transition: 'all .15s ease',
                      aspectRatio: '16 / 11',
                      boxShadow:
                        hoverMediaId === mid ? '0 4px 14px rgba(13,110,253,.12)' : 'none',
                      borderColor:
                        hoverMediaId === mid ? 'rgba(13,110,253,.35)' : 'rgba(0,0,0,.08)',
                    }}
                  >
                    {src ? (
                      audio ? (
                        <div className="h-100 w-100 d-flex align-items-center justify-content-center">
                          <i className="bi bi-music-note-beamed fs-2 text-secondary" />
                        </div>
                      ) : video ? (
                        <video
                          src={src}
                          muted
                          playsInline
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <img
                          src={src}
                          alt=""
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      )
                    ) : (
                      <div className="h-100 w-100 d-flex align-items-center justify-content-center text-muted">
                        <i className="bi bi-image fs-3" />
                      </div>
                    )}

                    {/* Type icon only */}
                    <div
                      className="position-absolute d-flex align-items-center justify-content-center"
                      style={{
                        left: 8,
                        top: 8,
                        width: 24,
                        height: 24,
                        borderRadius: 999,
                        background: 'rgba(255,255,255,.92)',
                        border: '1px solid rgba(0,0,0,.06)',
                      }}
                    >
                      <i
                        className={
                          'bi ' +
                          (audio ? 'bi-music-note-beamed' : video ? 'bi-camera-video' : 'bi-image')
                        }
                      />
                    </div>

                    {/* Add icon only */}
                    <div
                      className="position-absolute d-flex align-items-center justify-content-center"
                      style={{
                        right: 8,
                        bottom: 8,
                        width: 32,
                        height: 32,
                        borderRadius: 999,
                        background:
                          hoverMediaId === mid ? 'rgba(13,110,253,1)' : 'rgba(255,255,255,.92)',
                        color: hoverMediaId === mid ? '#fff' : '#0d6efd',
                        border: '1px solid rgba(13,110,253,.25)',
                        transition: 'all .15s ease',
                      }}
                    >
                      <i className="bi bi-plus-lg" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination */}
        <div className="d-flex align-items-center justify-content-between mt-2">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={mediaPage <= 1}
            onClick={() => setMediaPage((p) => Math.max(1, p - 1))}
          >
            <i className="bi bi-chevron-left" />
          </button>
          <span className="small text-muted">
            Page {mediaPage} / {totalMediaPages}
          </span>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={mediaPage >= totalMediaPages}
            onClick={() => setMediaPage((p) => Math.min(totalMediaPages, p + 1))}
          >
            <i className="bi bi-chevron-right" />
          </button>
        </div>
      </>
    );
  };

  /* ------------------------------ Timeline UI ------------------------------ */

  const renderSegmentBlock = (seg, index) => {
    const m = seg.media;
    const src = seg.kind === 'media' ? buildMediaSrc(m) : '';

    const left = seg.start * pxPerSecond;
    const width = Math.max(seg.duration * pxPerSecond, 36);

    const isActive = isPlaying ? index === activeIndex : index === selectedIndex;

    const bgByKind =
      seg.kind === 'text'
        ? 'linear-gradient(135deg, rgba(13,110,253,.12), rgba(13,110,253,.02))'
        : seg.kind === 'element'
        ? 'linear-gradient(135deg, rgba(255,193,7,.18), rgba(255,193,7,.04))'
        : seg.kind === 'tts'
        ? 'linear-gradient(135deg, rgba(25,135,84,.14), rgba(25,135,84,.03))'
        : '#f7f9fd';

    const label =
      seg.kind === 'media'
        ? m?.title || 'Clip'
        : seg.kind === 'text'
        ? seg.payload?.text || 'Text'
        : seg.kind === 'element'
        ? seg.payload?.label || 'Element'
        : 'TTS';

    return (
      <div
        key={`${seg.kind}-${seg.mediaId || seg.payload?.overlayId || index}-${index}`}
        role="button"
        tabIndex={0}
        draggable
        onDragStart={(e) => handleSegmentDragStart(e, index)}
        onDragOver={allowDrop}
        onDrop={(e) => handleBlockDrop(e, index)}
        onClick={() => {
          setSelectedIndex(index);
          setCurrentTime(seg.start);
          setIsPlaying(false);
        }}
        className="position-absolute"
        style={{
          left,
          top: 6,
          width,
          height: 44,
          borderRadius: 8,
          overflow: 'hidden',
          cursor: 'pointer',
          border: isActive ? '1px solid rgba(59,130,246,.9)' : '1px solid rgba(0,0,0,.06)',
          boxShadow: isActive ? '0 0 0 1px rgba(59,130,246,.25) inset' : 'none',
          background: bgByKind,
        }}
        title={label}
      >
        {/* Visual for media blocks only */}
        {seg.kind === 'media' ? (
          src ? (
            isAudio(m) ? (
              <div className="h-100 w-100 d-flex align-items-center justify-content-center">
                <i className="bi bi-soundwave text-success" />
              </div>
            ) : isVideo(m) ? (
              <video
                src={src}
                muted
                playsInline
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            )
          ) : (
            <div className="h-100 w-100 d-flex align-items-center justify-content-center text-muted">
              <i className="bi bi-image" />
            </div>
          )
        ) : (
          <div className="h-100 w-100 d-flex align-items-center px-2">
            <i
              className={
                'bi me-1 ' +
                (seg.kind === 'text'
                  ? 'bi-fonts text-primary'
                  : seg.kind === 'element'
                  ? 'bi-stars text-warning'
                  : 'bi-soundwave text-success')
              }
            />
            <span className="small text-truncate" style={{ maxWidth: '90%' }}>
              {label}
            </span>
          </div>
        )}

        {/* Selected label badge */}
        {isActive && (
          <div
            className="position-absolute"
            style={{
              left: 6,
              bottom: 6,
              padding: '2px 6px',
              fontSize: 9,
              borderRadius: 6,
              background: 'rgba(13,110,253,.9)',
              color: '#fff',
              maxWidth: '85%',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {label}
          </div>
        )}

        {/* Resize handle */}
        <div
          role="separator"
          title="Étire pour ajuster la durée"
          onMouseDown={(e) => startResize(e, index, seg.duration)}
          className="position-absolute"
          style={{
            right: 0,
            top: 0,
            width: 10,
            height: '100%',
            cursor: 'ew-resize',
            background: isActive
              ? 'linear-gradient(to left, rgba(13,110,253,.35), transparent)'
              : 'linear-gradient(to left, rgba(0,0,0,.08), transparent)',
          }}
        />
      </div>
    );
  };

  const renderTimelineLane = (track) => {
    const laneSegments = segments.filter(track.match);

    return (
      <div className="d-flex align-items-stretch" style={{ gap: 10 }}>
        <div
          className="d-flex align-items-center gap-2"
          style={{
            width: 110,
            flex: '0 0 auto',
            padding: '6px 8px',
            borderRadius: 10,
            background: '#ffffff',
            border: '1px solid #eef1f6',
          }}
        >
          <i className={`bi ${track.icon} text-primary`} />
          <span style={{ fontSize: 12 }}>{track.label}</span>
        </div>

        <div
          onDragOver={allowDrop}
          onDrop={handleLaneDrop}
          style={{
            position: 'relative',
            width: timelineWidth,
            height: 56,
            borderRadius: 10,
            background: '#ffffff',
            border: '1px solid #eef1f6',
            overflow: 'hidden',
          }}
        >
          {/* Soft grid */}
          {Array.from({ length: Math.ceil(timelineWidth / (pxPerSecond * 2)) }).map((_, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                left: i * pxPerSecond * 2,
                top: 0,
                width: 1,
                height: '100%',
                background: '#f2f4f8',
              }}
            />
          ))}

          {/* Blocks */}
          {laneSegments.map((seg) => {
            const index = segments.indexOf(seg);
            if (index < 0) return null;
            return renderSegmentBlock(seg, index);
          })}
        </div>
      </div>
    );
  };

  /* ------------------------------- Header UI ------------------------------- */

  const today = useMemo(() => {
    const d = new Date();
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  }, []);

  const handleExportClick = () => {
    addToast('Export final à gérer via votre page Montage/FFmpeg.', 'success');
  };

  const handlePlayPause = () => {
    if (!segments.length) return;
    setIsPlaying((p) => !p);
  };

  /* --------------------------------- Render -------------------------------- */

  return (
    <div style={{ paddingTop: embedded ? 0 : 90, paddingBottom: embedded ? 0 : 40 }}>
      <div className={embedded ? "" : "container-fluid"}>
        {!embedded && (
          <>
            {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <i className="bi bi-play-btn-fill text-primary" />
              <h2 className="h5 mb-0">Éditeur vidéo AI</h2>
              <span className="badge bg-light text-secondary">Beta</span>
            </div>
            <p className="text-muted mb-0 small">
              Media-first editor avec overlays (Text/Elements/TTS). L’export final est géré par le backend.
            </p>
            {projectId && (
              <p className="text-muted mb-0 small mt-1">
                Projet lié : <span className="fw-semibold">#{projectId}{project?.title ? ` — ${project.title}` : ''}</span>
              </p>
            )}
          </div>

          <div className="d-flex align-items-center gap-2">
            <span className="text-muted small d-none d-md-inline">{today}</span>
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleExportClick}>
              <i className="bi bi-cloud-arrow-up me-1" />Exporter
            </button>
            {projectId && (
              <Link to={`/project/${projectId}/manage`} className="btn btn-outline-secondary btn-sm">
                <i className="bi bi-arrow-left me-1" />Retour projet
              </Link>
            )}
          </div>
        </div>
          </>
        )}

        {error && (
          <div className="alert alert-warning">
            <i className="bi bi-exclamation-triangle me-1" />{error}
          </div>
        )}

        {/* MAIN EDITOR */}
        <div className="card shadow-sm border-0 rounded-4 mb-3">
          <div className="card-body">
            {embedded && (
              <div className="alert alert-secondary-subtle border rounded-4 small d-flex align-items-start gap-2 mb-3">
                <i className="bi bi-link-45deg fs-5 mt-1" />
                <div className="flex-grow-1">
                  <div className="fw-semibold">Montage importé depuis Weavevo</div>
                  <div className="text-muted">
                    Les médias, la voix et la musique sélectionnés dans JourneyAdmin sont injectés ici pour vous aider à affiner votre timeline.
                  </div>
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    <span className="badge bg-light text-dark">
                      {(montageParams?.selectedMediaIds || montageParams?.media_order || []).length} média(s)
                    </span>
                    {externalVoice?.label && (
                      <span className="badge bg-light text-dark">
                        <i className="bi bi-mic me-1" />{externalVoice.label}
                      </span>
                    )}
                    {externalMusic?.label && (
                      <span className="badge bg-light text-dark">
                        <i className="bi bi-music-note-beamed me-1" />{externalMusic.label}
                      </span>
                    )}
                    {externalAudioMix && (
                      <span className="badge bg-light text-dark">
                        Mix: {externalAudioMix}
                      </span>
                    )}
                    {(externalMusicVolume !== null && typeof externalMusicVolume !== 'undefined') && (
                      <span className="badge bg-light text-dark">
                        Vol musique: {externalMusicVolume}{String(externalMusicVolume).includes('%') ? '' : '%'}
                      </span>
                    )}
                    {montageParams?.transition && (
                      <span className="badge bg-light text-dark">
                        Transition: {montageParams.transition}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div className="d-flex gap-3" style={{ alignItems: 'stretch', flexWrap: 'wrap' }}>
              {/* TOOLBOX (no Canvas) */}
              <div
                className="border rounded-4"
                style={{ width: 72, minHeight: 420, background: '#fff', padding: 8 }}
              >
                <div className="d-flex flex-column gap-1">
                  <ToolboxButton icon="bi-cloud-arrow-up" label="Media" active={toolActive === 'media'} onClick={() => setToolActive('media')} />
                  <ToolboxButton icon="bi-fonts" label="Text" active={toolActive === 'text'} onClick={() => setToolActive('text')} />
                  <ToolboxButton icon="bi-stars" label="Elements" active={toolActive === 'elements'} onClick={() => setToolActive('elements')} />
                  <ToolboxButton icon="bi-music-note-beamed" label="Audio" active={toolActive === 'audio'} onClick={() => setToolActive('audio')} />
                  <ToolboxButton icon="bi-camera-video" label="Videos" active={toolActive === 'video'} onClick={() => setToolActive('video')} />
                  <ToolboxButton icon="bi-image" label="Images" active={toolActive === 'image'} onClick={() => setToolActive('image')} />
                  <ToolboxButton icon="bi-mic" label="Record" active={toolActive === 'record'} onClick={() => setToolActive('record')} />
                  <ToolboxButton icon="bi-soundwave" label="TTS" active={toolActive === 'tts'} onClick={() => setToolActive('tts')} />
                </div>
              </div>

              {/* LEFT PANEL */}
              <div
                className="border rounded-4"
                style={{
                  flex: '1 1 320px',
                  maxWidth: 440,
                  minWidth: 280,
                  background: '#fff',
                  padding: 12,
                }}
              >
                {renderLeftPanel()}
              </div>

              {/* PREVIEW */}
              <div
                className="border rounded-4"
                style={{
                  flex: '2 1 520px',
                  minWidth: 320,
                  background: '#fff',
                  padding: 12,
                }}
              >
                <div className="d-flex flex-column align-items-center">
                  <div
                    className="border rounded-4 overflow-hidden position-relative d-flex align-items-center justify-content-center"
                    style={{ ...canvasAspectStyle, backgroundColor: '#000' }}
                  >
                    {/* Media preview */}
                    {selectedSrc ? (
                      selectedIsVideo ? (
                        <video
                          key={`${selectedSrc}-${activeIndex ?? 0}`}
                          src={selectedSrc}
                          controls
                          playsInline
                          className="w-100 h-100"
                          style={{ objectFit }}
                        />
                      ) : (
                        <img
                          src={selectedSrc}
                          alt={selectedMedia?.title || ''}
                          className="w-100 h-100"
                          style={{ objectFit }}
                        />
                      )
                    ) : (
                      <div className="text-white-50 small text-center px-3">
                        Ajoute des clips depuis la bibliothèque puis sélectionne un clip dans la timeline.
                      </div>
                    )}

                    {/* Text overlays */}
                    {textOverlays.map((t) => {
                      const def = TEXT_STYLES.find((s) => s.key === t.styleKey);
                      const base = def?.style || {};
                      const isSel = t.id === selectedTextId;
                      const color = t.styleKey === 'black' ? '#0b0f14' : '#ffffff';

                      return (
                        <div
                          key={t.id}
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTextId(t.id);
                            setToolActive('text');
                          }}
                          className="position-absolute"
                          style={{
                            left: `${t.x}%`,
                            top: `${t.y}%`,
                            transform: 'translate(-50%, -50%)',
                            color,
                            fontSize: base.fontSize || 22,
                            fontWeight: base.fontWeight || 700,
                            fontStyle: base.fontStyle,
                            textDecoration: base.textDecoration,
                            textTransform: base.textTransform,
                            letterSpacing: base.letterSpacing,
                            fontFamily: base.fontFamily,
                            padding: base.padding || 0,
                            borderRadius: base.borderRadius || 0,
                            background: base.background || 'rgba(0,0,0,.18)',
                            textShadow: '0 2px 10px rgba(0,0,0,.45)',
                            border: isSel ? '1px solid rgba(59,130,246,.9)' : '1px solid transparent',
                            cursor: 'pointer',
                            maxWidth: '90%',
                            textAlign: 'center',
                            userSelect: 'none',
                            lineHeight: 1.1,
                          }}
                        >
                          {t.text || 'Texte'}
                        </div>
                      );
                    })}

                    {/* Element overlays */}
                    {elementOverlays.map((el) => {
                      const isSel = el.id === selectedElementId;
                      const isEmoji = el.kind === 'emoji';

                      const commonStyle = {
                        left: `${el.x}%`,
                        top: `${el.y}%`,
                        transform: 'translate(-50%, -50%)',
                        border: isSel ? '1px solid rgba(59,130,246,.9)' : '1px solid transparent',
                        cursor: 'pointer',
                        userSelect: 'none',
                      };

                      if (isEmoji) {
                        return (
                          <div
                            key={el.id}
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedElementId(el.id);
                              setToolActive('elements');
                            }}
                            className="position-absolute"
                            style={{ ...commonStyle, fontSize: 34, padding: 2 }}
                          >
                            {el.label}
                          </div>
                        );
                      }

                      if (el.category === 'gifs') {
                        return (
                          <div
                            key={el.id}
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedElementId(el.id);
                              setToolActive('elements');
                            }}
                            className="position-absolute"
                            style={{
                              ...commonStyle,
                              padding: '6px 10px',
                              borderRadius: 10,
                              background: 'rgba(255,255,255,.92)',
                              fontSize: 12,
                              fontWeight: 800,
                            }}
                          >
                            {el.label}
                          </div>
                        );
                      }

                      // shapes default
                      return (
                        <div
                          key={el.id}
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedElementId(el.id);
                            setToolActive('elements');
                          }}
                          className="position-absolute"
                          style={{
                            ...commonStyle,
                            width: 120,
                            height: 44,
                            borderRadius: el.itemKey === 'circle' ? 999 : el.itemKey === 'pill' ? 999 : 12,
                            background: 'rgba(255,255,255,.15)',
                            borderStyle: el.itemKey === 'outline' ? 'dashed' : 'solid',
                            borderColor: isSel ? 'rgba(59,130,246,.9)' : 'rgba(255,255,255,.35)',
                          }}
                        />
                      );
                    })}
                  </div>

                  <div className="d-flex align-items-center gap-2 small text-muted w-100 mt-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={handlePlayPause}
                      disabled={!segments.length}
                    >
                      <i className={`bi ${isPlaying ? 'bi-pause-fill' : 'bi-play-fill'}`} /> Lecture
                    </button>
                    <span>
                      Aperçu symbolique. Le rendu final multi-clips est géré par votre backend.
                    </span>
                  </div>
                </div>
              </div>

              {/* RIGHT PANELS */}
              <div
                style={{
                  flex: '1 1 260px',
                  maxWidth: 360,
                  minWidth: 240,
                }}
              >
                {/* Transform + Transitions */}
                <div className="border rounded-4 mb-3 overflow-hidden bg-white">
                  <button
                    type="button"
                    className="btn w-100 text-start d-flex align-items-center justify-content-between"
                    style={{ padding: '10px 12px', background: '#f8fafc', borderRadius: 0 }}
                    onClick={() => setOpenTransform((s) => !s)}
                  >
                    <span className="fw-semibold small">
                      <i className="bi bi-sliders2-vertical me-2 text-primary" />Transform
                    </span>
                    <i className={`bi ${openTransform ? 'bi-chevron-up' : 'bi-chevron-down'}`} />
                  </button>

                  {openTransform && (
                    <div className="p-3">
                      {/* Fit (functional) */}
                      <div className="mb-3">
                        <label className="form-label small fw-semibold">Fit</label>
                        <div className="btn-group w-100" role="group">
                          <button
                            type="button"
                            className={`btn btn-sm ${previewFit === 'cover' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setPreviewFit('cover')}
                          >
                            Fill
                          </button>
                          <button
                            type="button"
                            className={`btn btn-sm ${previewFit === 'contain' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setPreviewFit('contain')}
                          >
                            Fit
                          </button>
                          <button
                            type="button"
                            className={`btn btn-sm ${previewFit === 'scale-down' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setPreviewFit('scale-down')}
                          >
                            Crop
                          </button>
                        </div>
                        <div className="small text-muted mt-1">
                          Appliqué au preview via <code>object-fit</code>.
                        </div>
                      </div>

                      {/* Orientation compact selector */}
                      <div className="mb-3">
                        <label className="form-label small fw-semibold">Orientation</label>
                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className={`btn btn-sm flex-grow-1 ${aspect === 'portrait' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setAspect('portrait')}
                          >
                            <i className="bi bi-phone me-1" />9:16
                          </button>
                          <button
                            type="button"
                            className={`btn btn-sm flex-grow-1 ${aspect === 'square' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setAspect('square')}
                          >
                            <i className="bi bi-square me-1" />1:1
                          </button>
                          <button
                            type="button"
                            className={`btn btn-sm flex-grow-1 ${aspect === 'landscape' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setAspect('landscape')}
                          >
                            <i className="bi bi-tv me-1" />16:9
                          </button>
                        </div>
                      </div>

                      {/* Transitions UI (attachment-inspired) */}
                      <div className="mb-2">
                        <label className="form-label small fw-semibold">Transitions</label>
                        <div className="d-flex flex-wrap gap-2">
                          {TRANSITIONS.map((t) => (
                            <button
                              key={t.key}
                              type="button"
                              className={`btn btn-sm ${defaultTransition === t.key ? 'btn-dark' : 'btn-outline-secondary'}`}
                              onClick={() => setDefaultTransition(t.key)}
                              style={{ borderRadius: 10 }}
                            >
                              <i className={`bi ${t.icon} me-1`} />{t.label}
                            </button>
                          ))}
                        </div>
                        <div className="small text-muted mt-1">
                          Utilisé comme transition par défaut pour les nouveaux segments.
                        </div>
                      </div>

                      {/* Selected segment summary */}
                      <div className="border rounded-4 p-2" style={{ background: '#fbfdff' }}>
                        <div className="small fw-semibold">Sélection</div>
                        <div className="small text-muted">
                          {selectedSegment && selectedSegment.kind ? (
                          `${String(selectedSegment.kind).toUpperCase()} • ${Math.round(selectedSegment.duration || 0)}s` +
                          (selectedSegment.kind === 'media' && selectedMedia?.title ? ` • ${selectedMedia.title}` : '')
                        ) : (
                          'Sélectionne un segment dans la timeline.'
                        )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* AI Panel enriched */}
                <div className="border rounded-4 overflow-hidden bg-white">
                  <button
                    type="button"
                    className="btn w-100 text-start d-flex align-items-center justify-content-between"
                    style={{ padding: '10px 12px', background: '#f8fafc', borderRadius: 0 }}
                    onClick={() => setOpenAiPanel((s) => !s)}
                  >
                    <span className="fw-semibold small">
                      <i className="bi bi-stars me-2 text-primary" />Montage IA
                    </span>
                    <i className={`bi ${openAiPanel ? 'bi-chevron-up' : 'bi-chevron-down'}`} />
                  </button>

                  {openAiPanel && (
                    <div className="p-3">
                      <div className="small text-muted mb-2">
                        Options de montage pour une génération plus proche d’un vrai monteur.
                      </div>

                      <div className="mb-2">
                        <div className="form-check form-check-sm">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="aiMode"
                            id="aiModeLinear"
                            checked={aiMode === 'linear'}
                            onChange={() => setAiMode('linear')}
                          />
                          <label className="form-check-label small" htmlFor="aiModeLinear">
                            <strong>Classique</strong>
                          </label>
                        </div>
                        <div className="form-check form-check-sm">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="aiMode"
                            id="aiModeSocial"
                            checked={aiMode === 'social'}
                            onChange={() => setAiMode('social')}
                          />
                          <label className="form-check-label small" htmlFor="aiModeSocial">
                            <strong>Social</strong>
                          </label>
                        </div>
                        <div className="form-check form-check-sm">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="aiMode"
                            id="aiModeStory"
                            checked={aiMode === 'story'}
                            onChange={() => setAiMode('story')}
                          />
                          <label className="form-check-label small" htmlFor="aiModeStory">
                            <strong>Storytelling</strong>
                          </label>
                        </div>
                      </div>

                      <div className="border rounded-4 p-2 mb-2" style={{ background: '#fbfdff' }}>
                        <div className="form-check form-check-sm">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="aiUseTransitions"
                            checked={aiUseTransitions}
                            onChange={(e) => setAiUseTransitions(e.target.checked)}
                          />
                          <label className="form-check-label small" htmlFor="aiUseTransitions">
                            Utiliser des transitions
                          </label>
                        </div>
                        <div className="form-check form-check-sm">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="aiPrioritizeVideos"
                            checked={aiPrioritizeVideos}
                            onChange={(e) => setAiPrioritizeVideos(e.target.checked)}
                          />
                          <label className="form-check-label small" htmlFor="aiPrioritizeVideos">
                            Prioriser les vidéos
                          </label>
                        </div>
                        <div className="form-check form-check-sm">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="aiAutoFit"
                            checked={aiAutoFit}
                            onChange={(e) => setAiAutoFit(e.target.checked)}
                          />
                          <label className="form-check-label small" htmlFor="aiAutoFit">
                            Auto-fit (Fill)
                          </label>
                        </div>
                      </div>

                      <div className="mb-2">
                        <label className="form-label small fw-semibold">Max clips</label>
                        <input
                          type="range"
                          min={6}
                          max={60}
                          step={1}
                          className="form-range"
                          value={aiMaxClips}
                          onChange={(e) => setAiMaxClips(Number(e.target.value))}
                        />
                        <div className="small text-muted">{aiMaxClips} clips max</div>
                      </div>

                      <div className="mb-3">
                        <label className="form-label small fw-semibold">Style intensity</label>
                        <input
                          type="range"
                          min={0}
                          max={100}
                          step={1}
                          className="form-range"
                          value={aiStyleIntensity}
                          onChange={(e) => setAiStyleIntensity(Number(e.target.value))}
                        />
                        <div className="small text-muted">{aiStyleIntensity}%</div>
                      </div>

                      <button
                        type="button"
                        className="btn btn-primary btn-sm w-100 mb-2"
                        disabled={aiLoading || !mediaList.length}
                        onClick={handleAiAutoMontage}
                      >
                        {aiLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                            Montage IA…
                          </>
                        ) : (
                          <>
                            <i className="bi bi-magic me-1" />Proposer un montage IA
                          </>
                        )}
                      </button>

                      <div className="small text-muted">
                        {aiMessage || 'Si le backend IA n’est pas disponible, une suggestion locale est utilisée.'}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* TIMELINE (multi-lanes always visible) */}
        <div className="card shadow-sm border-0 rounded-4">
          <div className="card-body">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="btn-group btn-group-sm" role="group">
                <button type="button" className="btn btn-outline-secondary" disabled={!segments.length}>
                  <i className="bi bi-scissors" />
                </button>
                <button type="button" className="btn btn-outline-secondary" disabled={!segments.length}>
                  <i className="bi bi-files" />
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  disabled={!segments.length}
                  onClick={clearTimeline}
                >
                  <i className="bi bi-trash3" />
                </button>
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  disabled={selectedIndex == null}
                  onClick={() => selectedIndex != null && removeSegment(selectedIndex)}
                >
                  <i className="bi bi-x-lg" />
                </button>
              </div>

              <div className="d-flex align-items-center gap-2 small text-muted">
                <i className="bi bi-clock-history" />
                <span>{formatTime(currentClamped)} / {formatTime(totalDuration)}</span>
              </div>

              <div className="btn-group btn-group-sm" role="group">
                <button type="button" className="btn btn-outline-secondary" disabled>
                  <i className="bi bi-dash" />
                </button>
                <button type="button" className="btn btn-outline-secondary" disabled>
                  <i className="bi bi-plus" />
                </button>
                <button type="button" className="btn btn-outline-secondary" disabled>
                  <i className="bi bi-arrows-angle-expand" />
                </button>
              </div>
            </div>

            <div
              onDragOver={allowDrop}
              onDrop={handleLaneDrop}
              style={{
                borderRadius: 12,
                border: '1px solid #e5e7eb',
                backgroundColor: '#f8fafc',
                padding: '10px 8px',
                overflowX: 'auto',
              }}
            >
              {/* Time ruler */}
              <div
                style={{
                  position: 'relative',
                  height: 18,
                  marginBottom: 10,
                  width: timelineWidth,
                }}
              >
                {Array.from({ length: Math.ceil(totalDuration) + 1 }).map((_, i) => {
                  const left = i * pxPerSecond;
                  return (
                    <div
                      key={i}
                      style={{
                        position: 'absolute',
                        left,
                        top: 0,
                        width: 1,
                        height: i % 5 === 0 ? 12 : 7,
                        backgroundColor: '#d6dbe3',
                      }}
                    >
                      {i % 5 === 0 && (
                        <span
                          style={{
                            position: 'absolute',
                            top: 12,
                            left: 2,
                            fontSize: 10,
                            color: '#9aa3af',
                          }}
                        >
                          {formatTime(i)}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Playhead */}
              <div style={{ position: 'relative', width: timelineWidth, height: 0 }}>
                <div
                  style={{
                    position: 'absolute',
                    left: playheadLeft,
                    top: -190,
                    width: 2,
                    height: 210,
                    backgroundColor: '#3b82f6',
                  }}
                />
              </div>

              {/* Lanes always visible */}
              <div className="d-flex flex-column" style={{ gap: 10 }}>
                {visibleTracks.length === 0 ? (
                  <div className="small text-muted px-2">
                    Ajoute un média, un texte, un élément ou une voix TTS pour afficher la/les timeline(s) associée(s).
                  </div>
                ) : (
                  visibleTracks.map((t) => (
                    <div key={t.id}>{renderTimelineLane(t)}</div>
                  ))
                )}
              </div>

              {/* Drop zone end */}
              <div
                onDragOver={allowDrop}
                onDrop={(e) => handleBlockDrop(e, segments.length)}
                className="mt-3"
                style={{
                  width: timelineWidth,
                  height: 28,
                  borderRadius: 8,
                  background: '#ffffff',
                  border: '1px dashed #dbe1ea',
                }}
                title="Dépose ici pour ajouter à la fin"
              />
            </div>

            {loading && (
              <div className="mt-3 text-muted small">
                <i className="bi bi-arrow-clockwise me-1" />Chargement de l’éditeur vidéo AI…
              </div>
            )}
          </div>
        </div>

        <Toasts />
      </div>
    </div>
  );
}
